import type { DropTableItem } from "@loot-os/api-contract";
import type { Weighting } from "@loot-os/core/boxProcessing/weighting";

import {
  type TenantAssetUploadStart,
  type TenantAssetUploadTicket,
  uploadTenantAssetFile,
} from "./tenantAssets";

export type BoxStatus = "draft" | "active" | "archived";

/** The five art columns beside `imageUrl`, as `boxes.update` names them. "" = none. */
export const BOX_ART_FIELDS = [
  "backgroundImageUrl",
  "overlayImageUrl",
  "mobileImageUrl",
  "mobileBackgroundImageUrl",
  "mobileOverlayImageUrl",
] as const;
export type BoxArtField = (typeof BOX_ART_FIELDS)[number];
export type BoxArtUrls = Record<BoxArtField, string>;

/**
 * One write, already bound to one box by the page that owns the router.
 *
 * The box editor is shared by two surfaces with different routers — a tenant's
 * own boxes (`boxes.*`, env-scoped) and the box templates (`platform.boxTemplates.*`,
 * global). `submit` deliberately takes no `boxId`: the shared components then
 * *cannot* address the wrong box or reach the wrong router, and each page names
 * its own procedures at the call site where they are type-checked.
 */
export interface BoxWriteHandle<TInput, TOutput = unknown> {
  /** `onSuccess` runs after the page's own, for the caller that needs the result. */
  submit: (input: TInput, opts?: { onSuccess?: (output: TOutput) => void }) => void;
  isPending: boolean;
}

/** Why a box cannot be deleted (`BOX_DELETE_BLOCKERS` on the server); null when it can. */
export type BoxDeleteBlocker = "opened" | "in_battle" | "enabled" | "purchased";

/**
 * The box fields either editor renders.
 *
 * `boxes.get` and `platform.boxTemplates.get` both return supersets of this, so
 * structural typing is the seam: the shared components never name a router's
 * output type, and neither router's extra fields (a tenant's `environmentId`, the
 * box template's `enabledCount`) reach them.
 */
export interface BoxEditorData {
  id: string;
  name: string;
  slug: string;
  imageUrl: string;
  backgroundImageUrl: string;
  overlayImageUrl: string;
  mobileImageUrl: string;
  mobileBackgroundImageUrl: string;
  mobileOverlayImageUrl: string;
  status: BoxStatus;
  featured: boolean;
  /** The box's own opener style; null = it inherits (shop, environment, platform). */
  openerStyleId: string | null;
  currentVersion: number;
  openedCount: number;
  /** A Date from a direct caller, a string once it has crossed the wire. */
  createdAt: Date | string;
  /** How the box was last priced, which seeds the editor's pricing mode. */
  weighting: Weighting;
  priceCents: number;
  currentVersionRow: { dropTable: EditorDropTableEntry[] } | null;
  /** Why the ⋯ menu offers Archive rather than Delete. */
  deleteBlocker: BoxDeleteBlocker | null;
}

/**
 * A stored entry as the box editors read it: the wire entry, plus the value the editor typed
 * for the row, in cents, when one is set. Only the dashboard reads return the override.
 */
export type EditorDropTableEntry = DropTableItem & {
  valueOverrideCents?: number;
  /** The catalog item's market price, or a listing's asking price, in cents. */
  marketPriceCents?: number;
  /** The weight the roll walks, present only when an out-of-stock exclusion moved it. */
  effectiveWeight?: number;
};

export interface BoxMetaPatch extends Partial<BoxArtUrls> {
  name?: string;
  imageUrl?: string;
  status?: BoxStatus;
  featured?: boolean;
  openerStyleId?: string | null;
}

/** One published drop-table row naming a catalog item, how often it is rolled, and its worth. */
export interface CatalogDropTableRowInput {
  source: "CATALOG";
  catalogItemId: string;
  weight: number;
  /** The operator's own worth for this row, replacing the catalog item's own. */
  valueCents?: number;
}

/**
 * One published drop-table row naming a vendor listing. It carries no value of its own: the
 * server prices a listing from the vendor's asking price plus the shipping that price excludes,
 * and that same figure is what the withdrawal price check compares the live price against.
 */
export interface MarketplaceDropTableRowInput {
  source: "MARKETPLACE";
  listingId: string;
  weight: number;
}

/** One published drop-table row, the same union the publish procedure accepts. */
export type DropTableRowInput = CatalogDropTableRowInput | MarketplaceDropTableRowInput;

/** A refresh names no fields: the box id the handle already holds is the whole input. */
export type BoxRefreshInput = Record<string, never>;

/** What one refresh did, as the two box editor pages read it for their message. */
export interface BoxRefreshResult {
  outcome: "version_written" | "stock_updated" | "unchanged";
  box: { currentVersion: number };
}

/** The message both box editors show after a refresh. */
export function refreshResultMessage(result: BoxRefreshResult): string {
  if (result.outcome === "version_written") {
    return `Version ${result.box.currentVersion} written`;
  }
  if (result.outcome === "stock_updated") return "Stock updated on the current version";
  return "No change";
}

export interface PublishVersionInput {
  dropTable: DropTableRowInput[];
  weighting: Weighting;
  /** Required by MANUAL and PRICE_TARGET; refused under WEIGHT_TARGET, which computes it. */
  priceCents?: number;
  /** The version the draft was built on; a box that moved past it refuses the publish. */
  expectedVersion?: number;
}

/** What a publish answers, as far as the editor reads it. */
export interface PublishVersionResult {
  box: { currentVersion: number };
}

/** What a save of the metadata answers. */
export interface BoxMetaResult {
  currentVersion: number;
}

/** Uploads one picked file as a piece of the box's art and answers the URL the draft stores. */
export type BoxArtUpload = (file: File) => Promise<string>;

/** Binds a router's `startArtUpload` / `finishArtUpload` pair to one box. */
export function boxArtUpload(
  boxId: string,
  start: {
    mutateAsync: (
      input: TenantAssetUploadStart & { boxId: string },
    ) => Promise<TenantAssetUploadTicket>;
  },
  finish: {
    mutateAsync: (input: { boxId: string; uploadKey: string }) => Promise<{ publicUrl: string }>;
  },
): BoxArtUpload {
  return async (file) => {
    const { publicUrl } = await uploadTenantAssetFile(
      file,
      (input) => start.mutateAsync({ ...input, boxId }),
      (uploadKey) => finish.mutateAsync({ boxId, uploadKey }),
    );
    return publicUrl;
  };
}

/** The box's art as the draft holds it, read off the stored box. */
export function boxArtUrls(box: BoxArtUrls): BoxArtUrls {
  return Object.fromEntries(BOX_ART_FIELDS.map((field) => [field, box[field]])) as BoxArtUrls;
}

/** What a duplicate answers: the copy, which the editor opens next. */
export interface BoxDuplicateResult {
  id: string;
}

/** Binds a tRPC mutation to one box. */
export function boxWrite<TInput extends object, TOutput = unknown>(
  boxId: string,
  mutation: {
    mutate: (
      input: TInput & { boxId: string },
      opts?: { onSuccess?: (output: TOutput) => void },
    ) => void;
    isPending: boolean;
  },
): BoxWriteHandle<TInput, TOutput> {
  return {
    submit: (input, opts) =>
      mutation.mutate({ ...input, boxId }, opts?.onSuccess ? { onSuccess: opts.onSuccess } : {}),
    isPending: mutation.isPending,
  };
}
